import React, { useEffect } from 'react';
import propTypes from 'prop-types';

import { residencePropTypesData } from '../../utils/DrupalEntityTypes/Residence/ResidencePropTypes';
import {
  assembleFloorPlanCoreNavBtnsData,
  assembleFloorPlanHeaderTextData,
  showPricingTrigger,
} from '../../utils/DrupalEntityTypes/FloorPlan/FloorPlanHelpers';
import { useAppStore, useConfigStore } from '../../stores';

import FadingContainer from '../FadingContainer/FadingContainer';
import DetailDialog from '../Detail/DetailDialog';
import DetailTable from '../Detail/DetailTable';
import FloorPlanDetailCore from '../FloorPlan/FloorPlanDetailCore';
import FavoriteButton from '../Personalization/Favorite/FavoriteButton';
import useResidenceDetail from './useResidenceDetail';

/**
 * MapResidenceDetail - Residence detail for viewing from within the map modal.
 * No embedded map is shown since we're already inside the map context.
 */
const MapResidenceDetail = ({
  data,
  uuid,
  setModalNavBtns,
  setModalHeaderData,
}) => {
  // Zustand stores for modal-specific needs
  const personalizationActive = useAppStore((state) => state.personalizationActive);
  const featureFlags = useConfigStore((state) => state.featureFlags);
  const { platformShowPricing, platformShowPricingInitially } = featureFlags || {};

  // Get shared residence detail data from hook
  const {
    unitData,
    isLoading,
    description,
    hasFees,
    floorPlanUuid,
    tableInfo,
    detailDialogData,
    pricingTriggered,
    setPricingTriggered,
  } = useResidenceDetail(uuid, data);

  // Set modal nav buttons when data changes
  useEffect(() => {
    if (isLoading || !unitData) {
      setModalNavBtns(null);
    } else {
      const rawNavBtns = assembleFloorPlanCoreNavBtnsData(unitData);
      setModalNavBtns(rawNavBtns);
    }
  }, [unitData, isLoading, setModalNavBtns]);

  // Assemble modal header data
  useEffect(() => {
    if (!setModalHeaderData) return;

    if (unitData && !isLoading) {
      // Full API data available - show complete header
      const rawShowPricingTrigger = showPricingTrigger(
        pricingTriggered,
        platformShowPricing,
        platformShowPricingInitially,
        hasFees,
      );

      // Create FavoriteButton component for header when personalization is active
      const headerComponents = personalizationActive ? [
        <FavoriteButton
          key="favorite-residence"
          type="residence"
          uuid={uuid}
          floorPlanUuid={floorPlanUuid}
          hasShadow={false}
          className="detailHeader__favorite"
        />,
      ] : [];

      // Residence detail is per-unit; sold out tag is for floor-plan-level inventory only — never show in map modal
      const dataForHeader = { ...unitData, remainingInventory: null, soldOut: null };
      const rawDetailHeaderText = assembleFloorPlanHeaderTextData(
        dataForHeader,
        null,
        null,
        false,
        rawShowPricingTrigger,
        setPricingTriggered,
        headerComponents,
      );

      setModalHeaderData(rawDetailHeaderText);
    } else {
      // Show skeleton while loading
      setModalHeaderData({
        titleParts: null,
        subtitles: null,
        tags: [],
        btns: [],
        components: [],
      });
    }
  }, [
    unitData,
    isLoading,
    hasFees,
    floorPlanUuid,
    pricingTriggered,
    platformShowPricing,
    platformShowPricingInitially,
    setPricingTriggered,
    setModalHeaderData,
    personalizationActive,
    uuid,
  ]);

  return (
    <FadingContainer>
      <div>
        <DetailDialog detailDialogData={detailDialogData} />

        <DetailTable
          id="info-scroll"
          listData={tableInfo}
          tableDescription={description}
          className="detailSection"
        />

        <FloorPlanDetailCore
          data={unitData}
          minimalData={data}
          isLoadingPhotoGallery={!unitData}
          isLoadingVideo={!unitData}
          isLoadingVirtualTour={!unitData}
          parent="modal"
          contentType="residence"
        />
      </div>
    </FadingContainer>
  );
};

MapResidenceDetail.propTypes = {
  uuid: propTypes.string,
  setModalNavBtns: propTypes.func,
  setModalHeaderData: propTypes.func,
  data: residencePropTypesData,
};

MapResidenceDetail.defaultProps = {
  uuid: null,
  setModalNavBtns: null,
  setModalHeaderData: null,
  data: null,
};

export default MapResidenceDetail;
